import { useEffect, useState } from 'react';
import { api, type CarExpirePreview } from '../lib/api';
import { ErrorBox } from './DataTable';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const STAGE = { d60: '60일 전', d0: '당일' } as const;

/**
 * 차량검사 만료 알림톡(게시판 #62) — 기준정보 › 차량에서 연다.
 * 매일 10시에 **차주에게** 만료 60일 전·당일 알림톡이 간다(외부 소속 제외 · 가장 최근 편성의 차주 한 명).
 * 🔴 켜기 전에 이 창에서 오늘 누구에게 가는지·앞으로 며칠에 몇 건인지 먼저 본다. 켜기·지금 보내기는 관리자만(서버가 막는다).
 */
export default function CarExpirePanel({ isAdmin, onClose }: { isAdmin: boolean; onClose: () => void }) {
  const [data, setData] = useState<CarExpirePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.previewCarExpire().then((r) => { if (!cancelled) setData(r); }).catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [nonce]);

  async function toggle() {
    if (!data) return;
    setBusy('toggle'); setError(null); setNotice(null);
    try {
      const r = await api.setCarExpireEnabled({ enabled: !data.enabled });
      setNotice(r.enabled ? '켰습니다 — 내일부터 매일 10시에 보냅니다. 오늘분은 「지금 보내기」로 보낼 수 있습니다.' : '껐습니다 — 더 이상 보내지 않습니다.');
      setNonce((n) => n + 1);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }
  async function runNow() {
    setBusy('run'); setError(null); setNotice(null);
    try {
      const r = await api.runCarExpireNow();
      setNotice(`보냄 ${r.sent}건 · 실패 ${r.failed}건 · 건너뜀 ${r.skipped}건(이미 보냈거나 번호 없음)`);
      setNonce((n) => n + 1);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }

  const toSend = data?.rows.filter((r) => !r.sent && !r.reason).length ?? 0;
  const aheadTotal = data?.ahead.reduce((s, a) => s + a.d60 + a.d0, 0) ?? 0;
  const aheadMax = data?.ahead.reduce((m, a) => Math.max(m, a.d60 + a.d0), 0) ?? 0;
  return (
    <div className="card max-h-[85vh] space-y-3 overflow-auto p-5">
      <div className="flex items-start gap-3">
        <div>
          <h2 className="text-base font-bold">차량검사 만료 알림톡</h2>
          <p className="mt-1 text-xs text-ink-soft">
            매일 10시, 검사 만료 <b>60일 전</b>과 <b>당일</b>에 편성 차주에게 카카오 알림톡을 보냅니다(외부 소속 제외 · 같은 건은 한 번만).
          </p>
        </div>
        <button className="btn-ghost ml-auto" data-modal-close onClick={onClose}>닫기</button>
      </div>
      {error && <ErrorBox message={error} />}
      {notice && <p className="rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{notice}</p>}
      {!data && !error && <p className="text-sm text-ink-faint">불러오는 중…</p>}
      {data && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className={`rounded-full px-2.5 py-0.5 font-semibold ${data.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-canvas text-ink-soft'}`}>
              자동 발송 {data.enabled ? '켜짐' : '꺼짐'}
            </span>
            {data.switchedAt && <span className="text-xs text-ink-faint">{data.switchedAt} {data.switchedBy?.split('@')[0]}</span>}
            <span className={`rounded-full px-2.5 py-0.5 text-xs ${data.template.ready ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}
                  title="카카오 검수가 끝나야 보낼 수 있습니다. 검수 전에는 켜 두어도 아무것도 가지 않습니다.">
              템플릿 {data.template.code} · {data.template.ready ? '검수 완료' : data.template.status}
            </span>
            {isAdmin && (
              <span className="ml-auto flex gap-2">
                <button className={data.enabled ? 'btn' : 'btn-primary'} disabled={!!busy} onClick={() => void toggle()}>
                  {busy === 'toggle' ? '바꾸는 중…' : data.enabled ? '자동 발송 끄기' : '자동 발송 켜기'}
                </button>
                <button className="btn" disabled={!!busy || !data.enabled || !data.template.ready || toSend === 0} onClick={() => void runNow()}
                        title="오늘 보낼 차주에게 지금 보냅니다(09~21시). 이미 보낸 건은 다시 가지 않습니다.">
                  {busy === 'run' ? '보내는 중…' : `지금 보내기 (${toSend}건)`}
                </button>
              </span>
            )}
          </div>

          <section>
            <h3 className="mb-1 text-sm font-semibold">오늘({data.day}) 대상 {data.rows.length}대 · 보낼 것 {toSend}건</h3>
            {data.rows.length === 0 ? <p className="text-xs text-ink-faint">오늘은 대상이 없습니다.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-ink-soft">
                  <th className="py-1">차량번호</th><th>검사 만료일</th><th>단계</th><th>차주</th><th>번호</th><th>상태</th>
                </tr></thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.carId} className="border-t border-line">
                      <td className="py-1">{r.carNo}</td><td>{r.testExpire}</td>
                      <td>{STAGE[r.stage]}{r.stage === 'd60' && r.daysLeft !== 60 ? ` (${r.daysLeft}일)` : ''}</td>
                      <td>{r.ownerName ?? '—'}</td><td className="tabular-nums">{r.phone ?? '—'}</td>
                      <td className={r.sent ? 'text-emerald-700' : r.reason ? 'text-red-600' : 'text-ink-soft'}>{r.sent ? '보냄' : r.reason ?? '보낼 예정'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold">앞으로 60일 — 모두 {aheadTotal}건 · 하루 최대 {aheadMax}건</h3>
            <div className="flex flex-wrap gap-1 text-[11px]">
              {data.ahead.filter((a) => a.d60 + a.d0 > 0).map((a) => (
                <span key={a.day} className="rounded bg-canvas px-1.5 py-0.5" title={`60일 전 ${a.d60} · 당일 ${a.d0}`}>
                  {a.day.slice(5)} <b>{a.d60 + a.d0}</b>
                </span>
              ))}
            </div>
          </section>

          <details>
            <summary className="cursor-pointer text-sm font-semibold">보내는 문구 예시</summary>
            <pre className="mt-1 whitespace-pre-wrap rounded bg-canvas p-3 text-xs">{data.sample}</pre>
          </details>

          <section>
            <h3 className="mb-1 text-sm font-semibold">최근 발송 기록</h3>
            {data.recent.length === 0 ? <p className="text-xs text-ink-faint">아직 보낸 적이 없습니다.</p> : (
              <table className="w-full text-xs">
                <tbody>
                  {data.recent.map((r) => (
                    <tr key={r.id} className="border-t border-line">
                      <td className="py-1">{r.at}</td><td>{r.car_no}</td><td>{r.test_expire}</td><td>{STAGE[r.stage]}</td><td>{r.owner_name ?? '—'}</td>
                      <td className={r.status === 'sent' ? 'text-emerald-700' : 'text-red-600'} title={r.error ?? ''}>{r.status === 'sent' ? '보냄' : '실패'}</td>
                      <td className="text-ink-faint">{r.created_by === 'schedule' ? '자동' : r.created_by.split('@')[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
